import { CircleNotch } from '@phosphor-icons/react';
import { cn, TextInput, type TextInputProps } from '@reelplicate/ui';
import { type ElementType, useEffect, useMemo, useState } from 'react';

const DOMAIN = /^[\w-]+(\.[\w-]+)*\.[a-z]{2,}$/i;

/** "https://www.Oatly.com/uk" → "oatly.com"; null until it looks like a site. */
function siteHostname(value: string): string | null {
  const host = value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/[/?#].*$/, '');
  return DOMAIN.test(host) ? host : null;
}

// Wait for typing to pause, so "o", "oa", "oat.c"… don't each fetch an icon.
function useDebounced<T>(value: T, ms: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/**
 * Figma "Favicon placeholder" (282:25326): the site's icon in the 18px slot of
 * a Text Input's leading icon. A bg/muted square until the site is known, then
 * its favicon - from DuckDuckGo's icon service, which 404s when a site has
 * none, so we keep the square (Google's returns a generic globe instead).
 * Move behind our API once there's one, so typed sites don't leave the app.
 *
 * Reveal: the square stays underneath until the icon has actually loaded,
 * then the icon cross-blurs in over it (opacity + scale 0.85 → 1 + 2px blur,
 * 220ms ease-out-expo) as the square fades - no empty flash while it loads.
 * While a typed site is waiting to be looked up or its icon is downloading, a
 * CircleNotch spinner (the Button's loading spinner) holds the slot.
 * Reduced motion keeps the fade only and slows the spin.
 */
function Favicon({
  host,
  pending,
  className,
}: {
  host: string | null;
  /** A site has been typed but the lookup is still waiting for typing to pause. */
  pending: boolean;
  className?: string;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<string | null>(null);
  const fetching = host !== null && failed !== host;
  const shown = fetching && loaded === host;
  // Spinner from the first valid keystroke until the icon loads or fails.
  const loading = pending || (fetching && !shown);
  return (
    <span aria-hidden className={cn('relative block', className)}>
      <span
        data-slot="favicon-placeholder"
        className={cn(
          'bg-bg-muted border-border-subtle absolute inset-0 rounded-xs border',
          'transition-opacity duration-160 ease-out',
          (shown || loading) && 'opacity-0',
        )}
      />
      <CircleNotch
        data-slot="favicon-spinner"
        data-loading={loading || undefined}
        className={cn(
          'text-icon-tertiary absolute inset-0 size-full opacity-0',
          'transition-opacity duration-160 ease-out data-loading:opacity-100',
          // Same spinner and speed as the library Button's loading state.
          'animate-spin [animation-duration:800ms] motion-reduce:[animation-duration:1600ms]',
        )}
      />
      {fetching && (
        <img
          key={host}
          src={`https://icons.duckduckgo.com/ip3/${host}.ico`}
          alt=""
          data-loaded={loaded === host || undefined}
          className={cn(
            'absolute inset-0 size-full rounded-xs object-contain',
            'blur-[2px] scale-85 opacity-0',
            'data-loaded:blur-none data-loaded:scale-100 data-loaded:opacity-100',
            'transition-[opacity,scale,filter] duration-220 ease-[cubic-bezier(0.19,1,0.22,1)]',
            'motion-reduce:blur-none motion-reduce:scale-100',
          )}
          onLoad={() => setLoaded(host)}
          onError={() => setFailed(host)}
        />
      )}
    </span>
  );
}

/**
 * A Text Input `leadingIcon` showing the favicon of the site typed in `value`.
 * The component only changes identity when the site does, so typing inside a
 * site's address doesn't remount it.
 */
function useFaviconIcon(value: string): ElementType<{ className?: string }> {
  const typed = siteHostname(value);
  const host = useDebounced(typed, 400);
  const pending = typed !== null && typed !== host;
  return useMemo(
    () =>
      function SiteFavicon({ className }: { className?: string }) {
        return <Favicon host={host} pending={pending} className={className} />;
      },
    [host, pending],
  );
}

/** A website Text Input whose leading icon is the typed site's favicon. */
export function SiteInput({
  value,
  ...props
}: Omit<TextInputProps, 'leadingIcon' | 'value'> & { value: string }) {
  return <TextInput {...props} value={value} leadingIcon={useFaviconIcon(value)} />;
}
